import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ICON_HINT = {
  home: '🏠',
  receipt_long: '🧾',
  shopping_bag: '🛍️',
  today: '📅',
  campaign: '📢',
  star: '⭐',
  list_alt: '📋',
  settings: '⚙️',
  feedback: '💬',
  language: '🌐',
  person: '👤',
  notifications: '🔔',
  grid_view: '▦',
  play_circle: '▶',
};

function SortableChromeItem({
  item,
  index,
  total,
  onToggle,
  onMoveLeft,
  onMoveRight,
  onLabelChange,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.item_key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : item.is_visible ? 1 : 0.55,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        minWidth: 168,
        maxWidth: 200,
        p: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: item.is_visible ? 'divider' : 'warning.light',
        bgcolor: 'background.paper',
        boxShadow: isDragging ? 4 : 0,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
            {ICON_HINT[item.icon_key] || '•'}
          </Typography>
          <Chip
            size="small"
            label={item.item_key}
            sx={{ height: 20, fontSize: 10, maxWidth: 90 }}
          />
        </Stack>
        <TextField
          size="small"
          label="Label"
          value={item.label_en || ''}
          onChange={(e) => onLabelChange(item.item_key, e.target.value.slice(0, 120))}
          fullWidth
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Tooltip title={item.is_visible ? 'Hide (off)' : 'Show'}>
            <IconButton size="small" onClick={() => onToggle(item.item_key)} color={item.is_visible ? 'primary' : 'default'}>
              {item.is_visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Stack direction="row">
            <IconButton size="small" disabled={index === 0} onClick={() => onMoveLeft(item.item_key)}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled={index >= total - 1} onClick={() => onMoveRight(item.item_key)}>
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={item.is_visible !== false}
              onChange={() => onToggle(item.item_key)}
            />
          }
          label={item.is_visible !== false ? 'On' : 'Off'}
          sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
        />
      </Stack>
    </Box>
  );
}

/**
 * Drag + arrow reorder board for one chrome slot (bottom_nav | top_bar).
 */
export default function ShellChromeBoard({
  title,
  blurb,
  items,
  onChange,
  addableKeys = [],
}) {
  const [addKey, setAddKey] = useState('');
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const ordered = useMemo(
    () => [...(items || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [items],
  );

  const hiddenAddable = useMemo(
    () =>
      addableKeys.filter((key) => {
        const row = ordered.find((i) => i.item_key === key);
        return !row || row.is_visible === false;
      }),
    [addableKeys, ordered],
  );

  const renumber = (list) =>
    list.map((item, idx) => ({ ...item, sort_order: idx * 10 }));

  const updateList = (next) => onChange(renumber(next));

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ordered.findIndex((i) => i.item_key === active.id);
    const newIndex = ordered.findIndex((i) => i.item_key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    updateList(arrayMove(ordered, oldIndex, newIndex));
  };

  const moveBy = (key, delta) => {
    const idx = ordered.findIndex((i) => i.item_key === key);
    if (idx < 0) return;
    const next = idx + delta;
    if (next < 0 || next >= ordered.length) return;
    updateList(arrayMove(ordered, idx, next));
  };

  const toggle = (key) => {
    updateList(
      ordered.map((i) =>
        i.item_key === key ? { ...i, is_visible: !i.is_visible } : i,
      ),
    );
  };

  const onLabelChange = (key, label_en) => {
    updateList(
      ordered.map((i) => (i.item_key === key ? { ...i, label_en } : i)),
    );
  };

  const handleAdd = () => {
    if (!addKey) return;
    const exists = ordered.find((i) => i.item_key === addKey);
    if (exists) {
      updateList(
        ordered.map((i) =>
          i.item_key === addKey ? { ...i, is_visible: true } : i,
        ),
      );
    }
    setAddKey('');
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
        mb: 2,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} mb={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={800}>
            {title}
          </Typography>
          {blurb ? (
            <Typography variant="body2" color="text.secondary">
              {blurb}
            </Typography>
          ) : null}
        </Box>
        {hiddenAddable.length > 0 ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Add screen</InputLabel>
              <Select
                label="Add screen"
                value={addKey}
                onChange={(e) => setAddKey(e.target.value)}
              >
                {hiddenAddable.map((key) => (
                  <MenuItem key={key} value={key}>
                    {key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={!addKey}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Stack>
        ) : null}
      </Stack>

      <Alert severity="info" sx={{ mb: 1.5 }}>
        Drag handles or ← → to reorder. Toggle Off to hide. Optional screens appear under Add.
        Routes stay in the app — only visibility, order, and labels are CMS-controlled.
      </Alert>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={ordered.map((i) => i.item_key)}
          strategy={horizontalListSortingStrategy}
        >
          <Stack direction="row" spacing={1.25} sx={{ overflowX: 'auto', pb: 1 }}>
            {ordered.map((item, index) => (
              <SortableChromeItem
                key={item.item_key}
                item={item}
                index={index}
                total={ordered.length}
                onToggle={toggle}
                onMoveLeft={(k) => moveBy(k, -1)}
                onMoveRight={(k) => moveBy(k, 1)}
                onLabelChange={onLabelChange}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
